/**
 * Emit debug values to inline markers in code snippets
 *
 * Finds elements with data-debug attributes matching the given id
 * and replaces their text content with the JSON-stringified value.
 */

import { type DevString, tightJavaScriptify } from "@phosphor/utils";
/**
 * You can replace values in a coderegion/codefile like the following might
 * be in a file that is used in a template.
 * ```ts
 * export type TodoListVM = {
 *   header: {
 *     newTodoText$: LiveQuery<string>; // !newTodoText
 *     updateNewTodoText: (text: string) => void;
 *     addTodo: () => void;
 *   };
 * ```
 *
 * @example
 * emitDebugValue("!newTodoText", dev``.ctx({ loc: "phosphor-blog/scripts/lib/dev/emitDebugValue.ts:22:35" }), text);
 */
export function emitDebugValue(id: `!${string}`, source: DevString, value: unknown): void {
  const elements = document.querySelectorAll(`[data-debug="${id}"]`);
  const jsonValue = tightJavaScriptify(value);

  for (const el of elements) {
    if (el instanceof HTMLElement) {
      const previousContent = el.textContent;

      if (value != null) {
        el.textContent = jsonValue;
      } else {
        el.textContent = "";
      }
      el.classList.add("debug-value-emitted");
      el.dataset.loc = String(source.toJSON().context?.loc);

      // Animate value change only if content actually changed
      if (previousContent !== el.textContent && previousContent !== null) {
        el.animate(
          [
            { offset: 0, backgroundColor: "transparent", transform: "scale(1)" } as Keyframe,
            { offset: 0.25, backgroundColor: "rgb(168, 85, 247, 0.3)", transform: "scale(1.05)" } as Keyframe,
            { offset: 1, backgroundColor: "transparent", transform: "scale(1)" } as Keyframe,
          ],
          { duration: 400, easing: "ease-in-out" },
        );
      }
    }
  }
}

/**
 * Returns a function that can be called to emit debug values.
 *
 * @example
 * const updater = emitDebugValueFn("!newTodoText", dev``.ctx({ loc: "phosphor-blog/scripts/lib/dev/emitDebugValue.ts:59:53" }))
 * updater(text)
 */
export function emitDebugValueFn(id: `!${string}`, source: DevString): (value: unknown) => void {
  return emitDebugValue.bind(null, id, source);
}
